import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Zap,
  BrainCircuit,
  LineChart,
  Mail,
  Calendar,
  Users,
  TrendingUp,
  ArrowRight,
  Smartphone,
} from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import hero from "@/assets/networking.jpg";
import capture from "@/assets/nfc-card-tap.jpg";
import follow from "@/assets/ai-abstract.jpg";
import pipeline from "@/assets/cards-hero.webp";

export const Route = createFileRoute("/vi/solutions/sales")({
  head: () => ({
    meta: [
      { title: "CRM & thẻ NFC cho đội bán hàng — banana.cards" },
      { name: "description", content: "Thu thập khách hàng tiềm năng chỉ với một lần chạm, để AI soạn email theo dõi, và giữ pipeline luôn chính xác. Banana là bộ công cụ bán hàng tất-cả-trong-một cho đội ngũ hiện đại." },
      { property: "og:title", content: "CRM & thẻ NFC cho đội bán hàng — banana.cards" },
      { property: "og:description", content: "Thu thập khách hàng tiềm năng chỉ với một lần chạm, để AI soạn email theo dõi, và giữ pipeline luôn chính xác." },
      { property: "og:image", content: hero },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/solutions/sales", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Cho đội bán hàng"
      title="Chốt nhiều hơn, gõ"
      italic="ít hơn."
      description="Banana ghi lại mọi cuộc trò chuyện, soạn email theo dõi và đồng bộ pipeline — để đội bán hàng dành cả ngày để bán hàng, không phải cập nhật CRM."
      heroImage={hero}
      metrics={[
        { v: "3×", l: "Theo dõi nhanh hơn" },
        { v: "+38%", l: "Tỷ lệ phản hồi so với mẫu lạnh" },
        { v: "12 giờ", l: "Tiết kiệm mỗi nhân viên/tuần" },
      ]}
      bullets={[
        { icon: Zap, title: "Thu thập lead trong 3 giây", desc: "Chạm, quét thẻ hoặc thả ảnh danh thiếp. Banana phân tích liên hệ, làm giàu dữ liệu từ LinkedIn và công ty, rồi thêm vào pipeline." },
        { icon: BrainCircuit, title: "AI theo dõi nghe như chính bạn", desc: "Học từ ghi chú cuộc họp, giọng văn và sản phẩm. Bản thảo xuất hiện trong hộp thư chỉ vài phút sau mỗi cuộc gọi." },
        { icon: LineChart, title: "Pipeline tự cập nhật", desc: "Cuộc họp tự chuyển giai đoạn. Việc cần làm được tạo từ các điểm hành động. Dự báo cuộn lên cấp quản lý — không cần chỉnh tay." },
      ]}
      deepDives={[
        {
          eyebrow: "Thu thập",
          title: "Biến mọi cái bắt tay",
          italic: "thành một bản ghi.",
          description: "Chạm thẻ NFC tại gian hàng, quét danh thiếp giấy trên Grab, hoặc chuyển tiếp một lời mời họp — Banana thu thập lead và định tuyến đến đúng nhân viên, pipeline và giai đoạn.",
          points: [
            "Chạm NFC từ bất kỳ iPhone hoặc Android nào",
            "Quét danh thiếp bằng AI (OCR + làm giàu dữ liệu)",
            "Phân bổ vòng tròn & định tuyến theo khu vực",
            "Tự động khử trùng lặp với tài khoản hiện có",
          ],
          image: capture,
        },
        {
          eyebrow: "Theo dõi",
          title: "AI gõ thay bạn,",
          italic: "bạn chỉ việc chốt.",
          description: "Sau mỗi cuộc họp, Banana viết bản tóm tắt cá nhân hóa, đề xuất bước tiếp theo, và xếp hàng chuỗi theo dõi tự điều chỉnh theo việc khách mở, nhấp hay đặt lịch.",
          points: [
            "Email tóm tắt sinh ra từ bản ghi cuộc họp",
            "Chuỗi tin nhắn rẽ nhánh theo tương tác",
            "A/B tiêu đề được kiểm thử theo từng persona",
            "Gửi một-lần-nhấp từ inbox hoặc CRM",
          ],
          image: follow,
          reverse: true,
        },
        {
          eyebrow: "Dự báo",
          title: "Một pipeline bạn",
          italic: "thực sự tin được.",
          description: "Giai đoạn tiến lên khi có tín hiệu thật — email được trả lời, cuộc họp được đặt, hợp đồng được xem. Không còn cảnh cuối quý vội vã cập nhật ô CRM mà không ai tin.",
          points: [
            "Tự động chuyển giai đoạn theo hoạt động thật",
            "Dự báo có trọng số đa tiền tệ",
            "Cảnh báo trượt & rủi ro cho deal kẹt",
            "Bảng điều khiển quản lý & bảng điểm nhân viên",
          ],
          image: pipeline,
        },
      ]}
      howItWorks={[
        { title: "Gửi thẻ đến đội ngũ", desc: "Thẻ NFC có thương hiệu, giao toàn cầu trong 5–7 ngày. Người nhận không cần cài app." },
        { title: "Kết nối inbox & lịch", desc: "Gmail, Outlook, Google & iCloud đồng bộ trong 2 cú nhấp. Dữ liệu CRM hiện có được nhập trong vài phút." },
        { title: "Thu thập & để AI làm việc", desc: "Nhân viên chạm. Cuộc họp được ghi chú. Bản thảo xuất hiện trong outbox. Pipeline tự di chuyển." },
        { title: "Huấn luyện bằng dữ liệu thật", desc: "Quản lý thấy ai đang theo dõi, ai không, và deal nào đang trượt — theo thời gian thực." },
      ]}
      capabilities={[
        "Thẻ NFC có thương hiệu",
        "AI ghi chú cuộc họp (Zoom/Meet/Teams)",
        "Email theo dõi tự tạo",
        "Pipeline với dự báo có trọng số",
        "Phân bổ lead vòng tròn",
        "Đồng bộ Salesforce & HubSpot",
        "Phân tích đội nhóm & bảng điểm",
        "SSO, SCIM & phân quyền theo vai trò",
      ]}
      faq={[
        { q: "Banana thực sự thu thập lead tại sự kiện như thế nào?", a: "Nhân viên chạm thẻ NFC vào bất kỳ điện thoại nào — không cần app. Khách hàng tiềm năng được dẫn đến hồ sơ có thương hiệu và có thể lưu liên hệ hoặc gửi lại thông tin của họ. Nếu họ gửi, lead xuất hiện trong pipeline với tên, chức danh, công ty, LinkedIn và các trả lời biểu mẫu tùy chỉnh. Với danh thiếp giấy, AI quét sẽ trích xuất các trường tương tự từ ảnh trong vài giây." },
        { q: "AI theo dõi thực sự viết gì?", a: "Sau một cuộc họp, AI tóm tắt nội dung trao đổi, các điểm khó của khách hàng, và bước tiếp theo đã thống nhất. Sau đó nó soạn email tóm tắt cá nhân hóa bằng giọng văn của bạn và xếp hàng chuỗi 5 lần chạm phù hợp với giai đoạn deal. Bạn có thể chỉnh sửa trước khi gửi, hoặc để nó tự gửi theo độ trễ." },
        { q: "Có đồng bộ với Salesforce hoặc HubSpot không?", a: "Có — đồng bộ hai chiều với Salesforce, HubSpot và Pipedrive. Liên hệ, tài khoản, cơ hội và hoạt động ánh xạ hai chiều. Bạn có thể giữ CRM hiện tại làm nguồn chính và dùng Banana làm lớp thu thập + AI, hoặc chạy độc lập." },
        { q: "Quản lý có thấy nhân viên đang làm gì không?", a: "Có. Bảng điều khiển quản lý hiển thị mọi cuộc họp, theo dõi và thay đổi giai đoạn theo từng nhân viên, kèm dự báo và chỉ số sức khỏe pipeline cấp đội. Gợi ý huấn luyện làm nổi bật deal chưa được chạm trong 7+ ngày hoặc thiếu bước tiếp theo bắt buộc." },
        { q: "Đội 50 người có thể bắt đầu nhanh đến mức nào?", a: "Thẻ giao trong vòng một tuần. Thiết lập — inbox, lịch, đồng bộ CRM, SSO — mất khoảng một giờ cho admin. Nhân viên thường bắt đầu thu thập lead ngay trong ngày nhận thẻ. Hầu hết các đội thấy email theo dõi đầu tiên do AI soạn trong vòng 24 giờ." },
      ]}
      outro={{
        title: "Trả lại cho nhân viên",
        italic: "cả tuần làm việc.",
        description: "Đừng trả lương cao cho người chốt sale để đi nhập dữ liệu. Banana thu thập, soạn và cập nhật — để đội ngũ tập trung bán hàng.",
      }}
      afterFaq={<UseCases tag="Bán hàng" />}
    />
  );
}

function UseCases({ tag }: { tag: string }) {
  const items = [
    { t: "Bán hàng thực địa & nhân viên theo khu vực", d: "Chạm để chia sẻ tại địa điểm, nhập CRM tức thì, theo dõi tự nhận biết thời gian di chuyển.", to: "/vi/platform/scout" },
    { t: "Bán hàng nội bộ & SDR", d: "Kết nối inbox + lịch, để AI soạn câu mở đầu cá nhân hóa và đặt cuộc họp.", to: "/vi/ai/email-writer" },
    { t: "Đội sự kiện & hội nghị", d: "Thu hàng trăm thẻ trong một ngày, định tuyến theo khu vực, theo dõi trước khi lên máy bay về.", to: "/vi/platform/events" },
    { t: "Account executive", d: "Tự động tóm tắt discovery call, tạo đề xuất & hợp đồng, dự báo tự tin.", to: "/vi/platform/contracts" },
  ];
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Tình huống sử dụng {tag}</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Mọi vai trò trong đội bạn, <em className="italic">đều được phục vụ.</em>
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map((i) => (
            <Link key={i.t} to={i.to} className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70">
              <h3 className="text-lg font-bold">{i.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{i.d}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                Khám phá <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}